import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { createPageUrl } from "@/utils";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Shield, Phone, KeyRound, ChevronRight, AlertCircle, Loader2, Clock } from "lucide-react";
import { getApiBaseUrl } from "@/lib/apiBase";
import { getFirstAccessiblePage } from "@/utils/systemModules";
import { motion } from "framer-motion";
import { useDeviceTier } from "@/hooks/useDeviceTier";

const API_BASE = getApiBaseUrl();

function pickLanding(user) {
  const roleKey = user?.role?.key || user?.role;

  if (roleKey === "PROMOTIONS_ADMIN") {
    return createPageUrl("Promotions");
  }

  if (roleKey === "HR_MANAGER") {
    return createPageUrl("Users");
  }

  if (roleKey === "AREA_MANAGER" || user?.custom_role === "area_manager") {
    return createPageUrl("AreaManagerStores");
  }

  return createPageUrl(getFirstAccessiblePage(user));
}

export default function LoginOtp() {
  const queryClient = useQueryClient();
  const { isConstrained } = useDeviceTier();

  const [step, setStep] = useState("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const api = useMemo(() => {
    return axios.create({
      baseURL: API_BASE,
      headers: { "Content-Type": "application/json" },
    });
  }, []);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handlePhoneSubmit = async (e) => {
    if (e) e.preventDefault();
    setError("");

    if (!phoneNumber || phoneNumber.length < 9) {
      setError("אנא הזן מספר טלפון תקין");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/request-otp", { phone: phoneNumber });
      setStep("code");
      setCountdown(30);
    } catch (err) {
      const raw = err?.response?.data;
      const d = typeof raw === "string" ? null : raw;
      const htmlHint =
        typeof raw === "string" && /cannot\s+post/i.test(raw)
          ? "נראה שהשרת על הפורט לא מכיר את /api/auth (או שזה לא השרת של הפרויקט). הרץ: lsof -i :5001 ובדוק GET /api/auth/health"
          : null;
      const errCode = d?.error;
      const map = {
        user_not_found: d?.message || "מספר הטלפון לא רשום במערכת.",
        user_blocked: d?.message || "משתמש חסום לכניסה.",
        too_many_requests: d?.message || "נסה שוב בעוד רגע.",
        route_not_found:
          d?.message ||
          "השרת לא מכיל את נתיב ההתחברות. ודא שרצת npm start מתוך תיקיית server ושאין תהליך אחר על פורט 5001.",
      };
      const msg =
        htmlHint ||
        (errCode && map[errCode]) ||
        d?.message ||
        (typeof d?.error === "string" && d.error !== "Not Found" ? d.error : null) ||
        (err?.response?.status === 404
          ? "בקשה נדחתה (404). פתח בדפדפן: /api/auth/health — אם אין JSON עם ok:true, זה לא ה-API הנכון על הפורט."
          : "שגיאה בשליחת הקוד");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = () => {
    if (countdown > 0) return;
    handlePhoneSubmit();
  };

  const handleCodeSubmit = async (e) => {
    if (e) e.preventDefault();
    setError("");
    if (!code || code.length < 6) {
      setError("אנא הזן קוד אימות מלא");
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", {
        phone: phoneNumber,
        code,
      });

      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("app_user", JSON.stringify(data.user));

      await queryClient.cancelQueries();
      queryClient.clear();

      window.location.href = pickLanding(data.user);
    } catch (err) {
      const d = err?.response?.data;
      const msg =
        d?.message ||
        (d?.error === "invalid_code" ? "קוד שגוי או שפג תוקף." : null) ||
        (typeof d?.error === "string" ? d.error : null) ||
        "קוד לא תקין. נסה שוב.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-[var(--app-vh,100dvh)] flex items-start ${isConstrained ? "justify-center p-4 pt-8 overflow-y-auto bg-emerald-50" : "sm:items-center justify-center p-4 pt-6 sm:py-8 overflow-y-auto bg-gradient-to-br from-emerald-50 via-teal-50 to-blue-50"}`}>
      {!isConstrained ? (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-20 left-20 w-72 h-72 bg-emerald-300/20 rounded-full blur-3xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-20 right-20 w-96 h-96 bg-teal-300/20 rounded-full blur-3xl"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      ) : null}

      <div className="w-full max-w-md relative z-10">
        <Card className={isConstrained ? "shadow-md border border-emerald-100 bg-white" : "shadow-2xl border-0 overflow-hidden backdrop-blur-sm bg-white/95"}>
          <div className={isConstrained ? "h-2 bg-emerald-600 rounded-t-xl" : "absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-500"} />

          <CardHeader className="text-center pb-4 pt-6">
            {isConstrained ? (
              <img
                src="/logo.png"
                alt="לוגו הרשת"
                width={192}
                height={64}
                decoding="async"
                className="w-48 h-auto object-contain mx-auto mb-2"
              />
            ) : (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.15 }}
                className="mx-auto mb-2 flex items-center justify-center"
              >
                <img src="/logo.png" alt="לוגו הרשת" className="w-48 h-auto object-contain" />
              </motion.div>
            )}
            <CardTitle className={isConstrained ? "text-2xl font-bold text-emerald-700" : "text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent"}>
              Reshet App
            </CardTitle>
            <p className="text-gray-500 mt-2">
              {step === "phone" ? "התחברות למערכת" : "הזן את הקוד שנשלח אליך"}
            </p>
          </CardHeader>

          <CardContent className="p-6">
            {step === "phone" ? (
              <form onSubmit={handlePhoneSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-emerald-600" />
                    מספר טלפון נייד
                  </label>
                  <Input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="05X-XXXXXXX"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value);
                      setError("");
                    }}
                    className="h-14 text-lg border-2 bg-white"
                    dir="ltr"
                  />
                </div>

                {error ? (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                ) : null}

                <Button
                  type="submit"
                  disabled={loading}
                  className={`w-full h-14 text-white font-bold text-lg ${
                    isConstrained
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : "bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 shadow-lg"
                  }`}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 ml-2 animate-spin" />
                      שולח קוד...
                    </>
                  ) : (
                    <>
                      <ChevronRight className="w-5 h-5 ml-2" />
                      שלח קוד התחברות
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleCodeSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-emerald-600" />
                    קוד אימות
                  </label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value);
                      setError("");
                    }}
                    className="text-center h-16 text-3xl font-bold tracking-widest border-2 bg-white"
                    maxLength={6}
                  />
                  <p className="text-xs text-gray-500 text-center">נשלח אל: {phoneNumber}</p>
                </div>

                {error ? (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                ) : null}

                <div className="space-y-3">
                  <Button
                    type="submit"
                    disabled={loading || code.length < 6}
                    className={`w-full h-14 text-white font-bold text-lg ${
                      isConstrained
                        ? "bg-emerald-600 hover:bg-emerald-700"
                        : "bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 shadow-lg"
                    }`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 ml-2 animate-spin" />
                        מאמת...
                      </>
                    ) : (
                      <>
                        <Shield className="w-5 h-5 ml-2" />
                        התחבר למערכת
                      </>
                    )}
                  </Button>

                  <Button type="button" variant="ghost" onClick={() => setStep("phone")} className="w-full h-11">
                    שינוי מספר טלפון
                  </Button>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={countdown > 0 || loading}
                    className={`text-sm font-medium ${
                      countdown > 0 || loading ? "text-gray-400" : "text-emerald-600 font-bold"
                    }`}
                  >
                    {countdown > 0 ? (
                      <span className="flex items-center gap-2 justify-center">
                        <Clock className="w-4 h-4" />
                        שליחה חוזרת בעוד {countdown} שניות
                      </span>
                    ) : (
                      "לא קיבלת קוד? שלח שוב"
                    )}
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <div className={`mt-6 p-4 rounded-xl border border-emerald-100 text-center ${isConstrained ? "bg-white" : "bg-white/60 backdrop-blur-sm"}`}>
          <p className="text-sm text-gray-600">גישה מוגבלת למשתמשים מורשים בלבד</p>
        </div>
      </div>
    </div>
  );
}
